import { createSubscriber } from 'svelte/reactivity'
import type { StoreApi } from 'zustand/vanilla'

// ---------------------------------------------------------------------------
// Zustand -> Svelte bridge
// ---------------------------------------------------------------------------
// The stores keep their zustand implementation verbatim, so every action and cross-store
// subscription is unchanged; only the READ side needs adapting.
//
// Built on `createSubscriber` rather than Svelte's store contract on purpose: `$foo`
// auto-subscribes only inside a component, and several readers here are plain rune modules.
// A createSubscriber-backed getter reads the same way in both, needs no component context,
// and tears its subscription down when the last reader stops using it.

/** Whole-state accessor. Wakes readers on every `setState`, like `useStore()` with no selector. */
export function storeState<T>(store: StoreApi<T>): { readonly value: T } {
  const subscribe = createSubscriber((update) => store.subscribe(update))
  return {
    get value() {
      subscribe()
      return store.getState()
    },
  }
}

/**
 * Selected-slice accessor, and the load-bearing one: `storeState` on a hot store — transit
 * holds every vehicle in the system, repopulated on each socket push — wakes every reader on
 * every poll. This wakes only on an actual change to the selected slice, so a component
 * reading `alerts.length` isn't woken by a vehicle moving.
 *
 * `equals` defaults to `Object.is`. Pass `shallow` when the selector builds a fresh
 * array/object each call, or it never compares equal and the throttling is lost.
 */
export function storeSelect<T, U>(
  store: StoreApi<T>,
  selector: (state: T) => U,
  equals: (a: U, b: U) => boolean = Object.is,
): { readonly value: U } {
  const subscribe = createSubscriber((update) => {
    let current = selector(store.getState())
    return store.subscribe((state) => {
      const next = selector(state)
      if (equals(current, next)) return
      current = next
      update()
    })
  })
  return {
    get value() {
      subscribe()
      return selector(store.getState())
    },
  }
}

/** Shallow array/object equality, for selectors that build a new collection each call. */
export function shallow<U>(a: U, b: U): boolean {
  if (Object.is(a, b)) return true
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false
  const ak = Object.keys(a as object)
  const bk = Object.keys(b as object)
  if (ak.length !== bk.length) return false
  return ak.every((k) =>
    Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
  )
}
